// ════════════════════════════════════════════════════════════════════════
//  Überblick: woran man ist und was als Nächstes zu tun ist
//
//  Die Startseite des Lehrgangs. Vorher landete man auf „Releases“ – der Seite
//  mit dem technischsten Namen und dem einzigen Knopf, der ohne Entwurf etwas
//  tat. Wer stattdessen zu den Lektionen ging, fand eine Sackgasse.
//
//  Den leeren Zustand gibt es nicht mehr: ohne Entwurf steht hier die letzte
//  veröffentlichte Fassung, und der Weg zum Ändern führt über die Leiste. Ganz
//  leer ist die Seite nur vor der allerersten Fassung.
// ════════════════════════════════════════════════════════════════════════

import { Link } from "react-router-dom";
import { useDraft } from "../../../shared/context/DraftContext.js";
import { coursePath, useCourseSlug } from "../../../shared/lib/coursePath.js";
import { ProblemList } from "../../validation/components/ProblemList.js";
import styles from "../../../shared/components/shell.module.css";
import own from "./draft.module.css";

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: "Europe/Berlin",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export function Overview() {
  const slug = useCourseSlug();
  const {
    loading,
    error,
    draft,
    bundle,
    latestPublished,
    notes,
    setNotes,
    problems,
    readOnly,
  } = useDraft();

  if (loading) return <p className={styles.empty}>Fassungen werden geladen …</p>;
  if (error) {
    return (
      <div className={`${styles.notice} ${styles.noticeError}`}>
        Die Fassungen konnten nicht geladen werden.
      </div>
    );
  }

  // Vor der allerersten Fassung. Der Knopf dafür steht in der Leiste – ein
  // zweiter hier führte zu der Frage, welcher der richtige ist.
  if (!bundle) {
    return (
      <div className={styles.card}>
        <h1 className={styles.title}>Noch keine Fassung</h1>
        <p className={styles.subtitle}>
          Es ist noch nichts veröffentlicht. „Ersten Entwurf anlegen“ oben startet mit dem
          eingebauten Lehrgang.
        </p>
      </div>
    );
  }

  const stages = bundle.lessons.reduce((n, l) => n + l.stages.length, 0);

  const facts = (
    <dl className={own.facts}>
      <div>
        <dt>Lektionen</dt>
        <dd>{bundle.lessons.length}</dd>
      </div>
      <div>
        <dt>Stufen</dt>
        <dd>{stages}</dd>
      </div>
      {!readOnly && (
        <div>
          <dt>Offene Hinweise</dt>
          <dd>{problems.length}</dd>
        </div>
      )}
    </dl>
  );

  // ── Nur lesen: die veröffentlichte Fassung ───────────────────────────
  if (!draft) {
    return (
      <div className={styles.card}>
        <h1 className={styles.title}>
          {bundle.title} · Fassung {latestPublished?.version}
        </h1>
        <p className={styles.subtitle}>
          Veröffentlicht am{" "}
          {latestPublished?.publishedAt
            ? dateFormat.format(new Date(latestPublished.publishedAt))
            : "–"}{" "}
          und damit unveränderlich. Zum Ändern legen Sie oben einen neuen Entwurf an – er
          beginnt als Kopie dieser Fassung.{" "}
          <Link to={coursePath(slug, "fassungen")}>Alle Fassungen</Link>
        </p>
        {facts}
        {latestPublished?.notes && (
          <p className={own.note}>Änderungsnotiz: {latestPublished.notes}</p>
        )}
      </div>
    );
  }

  // ── Ändern: der offene Entwurf ───────────────────────────────────────
  return (
    <>
      <div className={styles.card}>
        <h1 className={styles.title}>Entwurf, Fassung {draft.version}</h1>
        <p className={styles.subtitle}>
          {latestPublished
            ? `Kopie von Fassung ${latestPublished.version}, veröffentlicht ist er damit noch nicht.`
            : "Die erste Fassung dieses Lehrgangs."}{" "}
          <Link to={coursePath(slug, "fassungen")}>Alle Fassungen</Link>
        </p>
        {facts}
      </div>

      <div className={`${styles.card} ${styles.spaced}`}>
        <h2 className={own.section}>
          {problems.length === 0
            ? "Keine offenen Hinweise"
            : problems.length === 1
              ? "Ein offener Hinweis"
              : `${problems.length} offene Hinweise`}
        </h2>
        {problems.length === 0 ? (
          <p className={own.note}>
            Der Entwurf ist prüfbar. Zum Veröffentlichen muss außerdem alles gespeichert
            sein – das sagt die Leiste darüber.
          </p>
        ) : (
          <ProblemList problems={problems} />
        )}
      </div>

      <div className={`${styles.card} ${styles.spaced}`}>
        <h2 className={own.section}>Änderungsnotiz</h2>
        <p className={own.note}>
          Das liest die Lehrkraft beim Fassungswechsel – sie entscheidet danach, ob sie
          ihre Klasse umstellt.
        </p>
        <textarea
          className={`${styles.field} ${styles.multiline} ${styles.spaced}`}
          aria-label="Änderungsnotiz"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Was hat sich am Wortmaterial geändert?"
        />
      </div>
    </>
  );
}
